// Home is Today: what needs someone now, each tile a count that links to the page filtered to it. Bills, late POs and
// failed Shopify writes come straight from synced rows; the plan (Order now, At risk), runs needing attention and CM
// counts are worked out after the first paint, so landing here stays instant (the first plan takes about 0.5 s).
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { paymentsDue } from "../../../shared/bills.ts";
import { runResult } from "../../../shared/costs.ts";
import { addDays, planToday } from "../../../shared/day.ts";
import type { SuggestedPoHidden } from "../../../shared/db.gen.ts";
import { addPlanDays, fmt, type ItemPlanResult, inBucket, type PlanVendor, shortDate } from "../../../shared/planning.ts";
import { projectPath } from "../../../shared/projects.ts";
import { dayOf, useBillIndex } from "../lib/bills.ts";
import { useCostIndex } from "../lib/costs.ts";
import { dayText, money, plural } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { usePlan } from "../lib/planning.ts";
import { Link, navigate } from "../lib/router.tsx";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { layoutOf, useTableViews } from "../lib/table-views.ts";
import { Button, cx, Input, Kbd } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { Popover } from "../ui/Popover.tsx";
import { ColumnsMenu, type LayoutColumn } from "../ui/TableViews.tsx";
import { useShopifyWrites } from "./CatalogEditor.tsx";
import { CoverThrough } from "./CoverThrough.tsx"; // Cover through ---
import { useCms } from "./consignment/lib.ts";
import { useCountDay } from "./counts/CountsPage.tsx"; // Stock counts ---
import { daysLate } from "./po/lib.ts";

type Tone = "danger" | "warn" | "ok";

/**
 * What Today shows (Mitch, 2026-11-03: "Our staff being able to choose what the home page shows based on a role"):
 * each person's own (Show, kept like a table's columns: table_pref "/:Today"), starting from their role's (`off`: the
 * roles that start without it). The first is always shown.
 */
type Section = LayoutColumn & { off?: readonly string[] };
const SECTIONS: Section[] = [
  { key: "plan", header: "Order now and At risk" },
  { key: "suggested", header: "Suggested POs by vendor" },
  { key: "payments", header: "Payments due this week", off: ["inventory", "receiving"] },
  { key: "late", header: "Late POs" },
  { key: "stock_counts", header: "Stock counts today", off: ["receiving"] }, // Stock counts ---
  { key: "shopify", header: "Failed Shopify writes" },
  { key: "counts", header: "CM counts due", off: ["receiving"] },
  { key: "runs", header: "Runs needing attention", off: ["inventory", "receiving"] },
];

function useSections() {
  const tv = useTableViews("/:Today");
  const role = me()?.role ?? "";
  const sections = SECTIONS.map((x) => ({ ...x, optional: !!x.off?.includes(role) }));
  const layout = tv.pref ? layoutOf(tv.pref) : {};
  const shown = new Set(layout.shown ?? []);
  const hidden = new Set([...(layout.hidden ?? []), ...sections.filter((x) => x.optional && !shown.has(x.key)).map((x) => x.key)]);
  const on = (key: string) => key === SECTIONS[0].key || !hidden.has(key);
  const toggle = (key: string, show: boolean) => {
    const optional = sections.find((x) => x.key === key)?.optional;
    tv.savePref({
      layout: {
        ...layout,
        hidden: (layout.hidden ?? []).filter((k) => k !== key).concat(show || optional ? [] : [key]),
        shown: (layout.shown ?? []).filter((k) => k !== key).concat(show && optional ? [key] : []),
      },
    });
  };
  const menu = tv.enabled && (
    <ColumnsMenu
      columns={sections}
      hidden={hidden}
      onToggle={toggle}
      onShowAll={() => tv.savePref({ layout: { hidden: [], shown: sections.filter((x) => x.optional).map((x) => x.key) } })}
      label="Show"
    />
  );
  return { on, menu };
}

export function HomePage() {
  const first = me()?.name?.split(" ")[0];
  const { on, menu } = useSections();
  // The heavy tiles mount after the first paint: an effect runs once the page is on screen, and the timer lets the
  // browser show it before the plan is worked out. (Not animation frames: those wait while the tab is in the background.)
  const [later, setLater] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setLater(true), 0);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-8 py-8">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Today{first ? `, ${first}` : ""}</h1>
          <span className="ml-auto">{menu}</span>
        </div>
        <p className="mt-1 text-muted">
          {dayText(planToday())}: what needs someone. Each tile opens the page filtered to it. <Kbd>Ctrl</Kbd> <Kbd>K</Kbd> finds anything.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {later ? <PlanTiles /> : <Waiting labels={["Order now", "At risk"]} />}
          {on("payments") && <BillTiles />}
          {on("late") && <LatePos />}
          {on("stock_counts") && <StockCountsToday />}
          {on("shopify") && <ShopifyFailures />}
          {on("counts") && (later ? <CountsDue /> : <Waiting labels={["CM counts due"]} />)}
          {on("runs") && (later ? <RunsNeedingAttention /> : <Waiting labels={["Runs needing attention"]} />)}
        </div>
        {on("suggested") && later && <SuggestedPos />}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, to, tone }: { label: string; value: ReactNode; sub?: ReactNode; to: string; tone?: Tone }) {
  return (
    <Link to={to} className="group flex flex-col rounded-lg border border-line bg-surface p-4 hover:border-line-strong">
      <div className="text-muted">{label}</div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <span
          className={cx("text-2xl font-semibold tabular-nums", tone === "danger" && "text-danger", tone === "warn" && "text-warn", tone === "ok" && "text-ok")}
        >
          {value}
        </span>
        <ArrowRight className="mb-1 size-4 shrink-0 text-faint group-hover:text-text" />
      </div>
      {sub && <div className="mt-1 truncate text-[12px] text-muted">{sub}</div>}
    </Link>
  );
}

function Waiting({ labels }: { labels: string[] }) {
  return labels.map((l) => (
    <div key={l} className="rounded-lg border border-line bg-surface p-4">
      <div className="text-muted">{l}</div>
      <div className="mt-1 text-2xl font-semibold text-faint">…</div>
    </div>
  ));
}

/** Bought sell items to order now, and at risk (the Purchasing plan's own buckets, sell items as it shows them). */
function PlanTiles() {
  const plan = usePlan();
  const sell = plan.results.filter((r) => r.role !== "make" && r.item.kind === "sell");
  const now = sell.filter((r) => inBucket(r, "order_now"));
  const risk = sell.filter((r) => inBucket(r, "at_risk"));
  const vendors = new Set(now.flatMap((r) => (r.vendor ? [r.vendor.id] : []))).size;
  return (
    <>
      <Tile
        label="Order now"
        value={now.length}
        tone={now.length ? "danger" : "ok"}
        sub={now.length ? `${plural(now.length, "sell item")} from ${plural(vendors, "vendor")}` : "Nothing to order today"}
        to="/purchasing"
      />
      <Tile
        label="At risk"
        value={risk.length}
        tone={risk.length ? "warn" : "ok"}
        sub={risk.length ? "Ordered, but may run out before it lands" : "Nothing at risk"}
        to="/purchasing?bucket=at_risk"
      />
    </>
  );
}

/**
 * Bills due this week and overdue ones (the Payments due board's sections), deposits included: the board keeps every
 * deposit in its own section whatever its date, so those due within the week or late are added here.
 */
function BillTiles() {
  const ix = useBillIndex();
  const board = useMemo(() => paymentsDue(ix, { dayOf }), [ix]);
  const weekEnd = addDays(planToday(), 6);
  const deposits = board.sections.deposits.filter((r) => r.overdueDays > 0 || (r.due !== null && r.due <= weekEnd));
  const rows = [...board.sections.overdue, ...board.sections.this_week, ...deposits];
  const late = rows.filter((r) => r.overdueDays > 0);
  const amount = (list: typeof rows) => {
    const by = new Map<string, number>();
    for (const r of list) by.set(r.currency, (by.get(r.currency) ?? 0) + r.amount);
    return [...by].map(([cur, n]) => money(Math.round(n * 100) / 100, cur)).join(" + ") || "$0.00";
  };
  const thisWeek = rows.length - late.length;
  return (
    <Tile
      label="Payments due this week"
      value={rows.length ? amount(rows) : "None"}
      tone={late.length ? "danger" : rows.length ? "warn" : "ok"}
      sub={
        rows.length
          ? [
              thisWeek && `${thisWeek} this week`,
              late.length && `${late.length} overdue (${amount(late)})`,
              deposits.length && `${plural(deposits.length, "deposit")} among them`,
            ]
              .filter(Boolean)
              .join(" · ")
          : "Nothing due this week"
      }
      to="/bills?tab=due"
    />
  );
}

function LatePos() {
  const pos = useRows("po");
  const today = planToday();
  const late = pos.filter((p) => daysLate(p, today) > 0).sort((a, b) => daysLate(b, today) - daysLate(a, today));
  return (
    <Tile
      label="Late POs"
      value={late.length}
      tone={late.length ? "danger" : "ok"}
      sub={
        late.length
          ? `${late[0].number} is ${plural(daysLate(late[0], today), "day")} late${late.length > 1 ? `, and ${late.length - 1} more` : ""}`
          : "Every open PO is on time"
      }
      to="/pos?status=late"
    />
  );
}

/** Changes Shopify refused (or something in Shopify put back), and receipts that didn't reach Shopify. */
/** Stock counts --- today's list to count by hand (made the first time anyone who counts opens Today or the page). */
function StockCountsToday() {
  const day = useCountDay();
  return (
    <Tile
      label="Stock counts today"
      value={!day.made ? "…" : day.tasks.length ? (day.left ? `${day.left} left` : "Done") : "None"}
      tone={day.tasks.length && !day.left ? "ok" : undefined}
      sub={
        !day.made
          ? "Making today's list"
          : day.tasks.length
            ? `${plural(day.tasks.length, "SKU")}, about ${day.units.toLocaleString("en-US")} units${day.done ? ` · ${day.done} counted` : ""}`
            : "Nothing due today"
      }
      to="/counts"
    />
  );
}

function ShopifyFailures() {
  const { problems } = useShopifyWrites();
  const receipts = useRows("receipt").filter((r) => r.shopify_status === "failed");
  const stock = useRows("stock_change").filter((c) => c.status === "failed"); // Stock changes --- Shopify refused them
  const n = problems.length + receipts.length + stock.length;
  return (
    <Tile
      label="Failed Shopify writes"
      value={n}
      tone={n ? "danger" : "ok"}
      sub={
        n
          ? [
              problems.length && `${plural(problems.length, "edit")} on Sell items`,
              receipts.length && `${plural(receipts.length, "receipt")} not in Shopify stock`,
              stock.length && `${plural(stock.length, "stock change")} Shopify refused`,
            ]
              .filter(Boolean)
              .join(" · ")
          : "Everything reached Shopify"
      }
      to={problems.length || !receipts.length ? "/items/sell" : `/pos?open=${receipts[0].po_id}&tab=receipts`}
    />
  );
}

function CountsDue() {
  const cms = useCms();
  const due = [...cms.values()].filter((c) => c.counts.due.due || c.counts.pending.length > 0);
  return (
    <Tile
      label="CM counts due"
      value={due.length}
      tone={due.length ? "warn" : "ok"}
      sub={
        due.length
          ? due
              .map(
                (c) =>
                  `${c.location.name}: ${c.counts.pending.length ? "a count to review" : c.counts.due.reason.charAt(0).toLowerCase() + c.counts.due.reason.slice(1)}`,
              )
              .join(" · ")
          : "Every CM counted recently"
      }
      to={due.length ? `/consignment?cm=${due[0].location.id}` : "/consignment"}
    />
  );
}

/** Open runs with costs that need something (no category, no invoice, a foreign amount at a rate of 1). */
function RunsNeedingAttention() {
  const cx_ = useCostIndex();
  const runs = useRows("production_run");
  const projects = useRows("project");
  const needing = useMemo(
    () =>
      runs
        .filter((r) => r.status !== "closed")
        .flatMap((r) => {
          const n = runResult(cx_, r.id)?.attention.length ?? 0;
          return n ? [{ run: r, n }] : [];
        })
        .sort((a, b) => a.run.number.localeCompare(b.run.number)),
    [cx_, runs],
  );
  const first = needing[0];
  const project = first && projects.find((p) => p.id === first.run.project_id);
  return (
    <Tile
      label="Runs needing attention"
      value={needing.length}
      tone={needing.length ? "warn" : "ok"}
      sub={needing.length ? needing.map((x) => `${x.run.number}: ${plural(x.n, "cost")}`).join(" · ") : "No open run has a cost to fix"}
      to={project ? `${projectPath(project, "productions")}&run=${encodeURIComponent(first.run.number)}&rt=costs` : "/projects"}
    />
  );
}

/**
 * Suggested POs (Mitch, 2026-11-03: "the dashboard shows suggested purchase orders per vendor laid out"): the plan's
 * Order now for each vendor (what it buys; builds are the Make plan's), what it adds up to and when it would land, and
 * the person's cover-through day for every vendor or the vendor's own (Cover through). Start PO opens the plan on that
 * vendor, where its draft is made from the lines as ticked and typed there.
 */
function SuggestedPos() {
  const plan = usePlan();
  const pos = useRows("po");
  const today = plan.today;
  const groups = useMemo(() => {
    const by = new Map<string, { vendor: PlanVendor; rows: ItemPlanResult[] }>();
    for (const r of plan.results) {
      if (r.role === "make" || !r.vendor || !inBucket(r, "order_now") || r.qty <= 0) continue;
      const g = by.get(r.vendor.id) ?? { vendor: r.vendor, rows: [] };
      g.rows.push(r);
      by.set(r.vendor.id, g);
    }
    return [...by.values()]
      .map((g) => {
        const cost = new Map<string, number>();
        for (const r of g.rows) if (r.cost != null) cost.set(r.currency, (cost.get(r.currency) ?? 0) + r.cost);
        const lands = Math.max(...g.rows.map((r) => r.landsOn(0)));
        const cover = g.rows.find((r) => r.coverTo)?.coverTo ?? null;
        const coverFor = Math.max(...g.rows.map((r) => r.coverFor));
        const draft = pos.find((p) => p.vendor_id === g.vendor.id && p.status === "draft" && p.kind === "purchase" && p.sent_revision === null);
        const total = [...cost.values()].reduce((a, b) => a + b, 0);
        return { ...g, id: g.vendor.id, cost, lands, cover, coverFor, draft, total, units: g.rows.reduce((n, r) => n + r.qty, 0) };
      })
      .sort((a, b) => b.total - a.total);
  }, [plan, pos]);
  // Hidden or snoozed (each person's own): a snoozed card is back on its day.
  const hiddenRows = useRows("suggested_po_hidden");
  const myId = me()?.id;
  const hidden = new Map(
    hiddenRows.filter((h) => (!h.user_id || h.user_id === myId) && (h.until === null || h.until.slice(0, 10) > today)).map((h) => [h.vendor_id, h]),
  );
  const shown = groups.filter((g) => !hidden.has(g.vendor.id));
  type Group = (typeof groups)[number];
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the section's title
  const planFor = (g: Group) => `/purchasing?vendor=${g.vendor.id}&scope=all`;
  const costText = (g: Group) => [...g.cost].map(([cur, n]) => money(Math.round(n * 100) / 100, cur)).join(" + ");
  const columns: Column<Group>[] = [
    {
      key: "vendor",
      header: "Vendor",
      width: "minmax(170px,1.6fr)",
      value: (g) => g.vendor.short_name || g.vendor.name,
      cell: (g) => {
        const name = g.vendor.short_name || g.vendor.name;
        return (
          <>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[10px] font-semibold text-accent-text">
              {initials(name)}
            </span>
            <span className="truncate font-medium" title={g.vendor.name}>
              {name}
            </span>
          </>
        );
      },
    },
    {
      key: "po",
      header: "PO",
      headerTitle: "Opens the Purchasing plan on this vendor, where its draft is made from the lines as ticked and typed there",
      width: "130px",
      value: (g) => g.draft?.number ?? "",
      text: (g) => (g.draft ? `Add to ${g.draft.number}` : "Start PO"),
      cell: (g) => (
        <Link
          to={planFor(g)}
          onClick={(e) => e.stopPropagation()}
          className="flex shrink-0 items-center gap-1 text-[12px] font-medium text-accent-text hover:underline"
        >
          {g.draft ? `Add to ${g.draft.number}` : "Start PO"} <ArrowRight className="size-3.5" />
        </Link>
      ),
    },
    {
      key: "lands",
      header: "Lands",
      headerTitle: "When it would land, ordered today",
      width: "90px",
      value: (g) => g.lands,
      text: (g) => addPlanDays(today, g.lands),
      cell: (g) => <span className="text-muted">about {shortDate(addPlanDays(today, g.lands), today)}</span>,
    },
    { key: "skus", header: "SKUs", width: "64px", align: "right", value: (g) => g.rows.length, cell: (g) => fmt(g.rows.length) },
    { key: "units", header: "Units", width: "80px", align: "right", cell: (g) => fmt(g.units) },
    {
      key: "cover",
      header: "Covers to",
      width: "minmax(210px,1.2fr)",
      value: (g) => g.cover?.day ?? g.coverFor,
      text: (g) => (g.cover ? addPlanDays(today, g.cover.day) : `${g.coverFor}d cover`),
      cell: (g) => (
        <>
          <span className="truncate text-muted">{g.cover ? shortDate(addPlanDays(today, g.cover.day), today) : `${g.coverFor}d cover`}</span>
          {/* Its own popover: a click or a key in it is its own, never the row's */}
          <span className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <CoverThrough vendor={g.vendor} compact />
          </span>
        </>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      width: "minmax(110px,1fr)",
      align: "right",
      value: (g) => g.total,
      text: (g) => costText(g),
      cell: (g) => <span className="font-semibold tabular-nums">{g.cost.size ? costText(g) : <span className="font-normal text-faint">No prices</span>}</span>,
    },
    {
      key: "hide",
      header: "",
      width: "40px",
      value: () => "",
      cell: (g) => (
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <HideCard vendor={g.vendor} row={hiddenRows.find((h) => (!h.user_id || h.user_id === myId) && h.vendor_id === g.vendor.id)} today={today} />
        </span>
      ),
    },
  ];
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold">Suggested POs</h2>
        <span className="text-[12px] text-muted">What the plan says to order now, by vendor</span>
        <div ref={setSlot} className="flex items-center gap-1" />
        <span className="ml-auto flex items-center gap-2">
          {hidden.size > 0 && <HiddenCards hidden={[...hidden.values()]} groups={groups} today={today} />}
          <CoverThrough vendor={null} />
        </span>
      </div>
      <div className="mt-3 bg-surface">
        <DataTable
          fit
          rows={shown}
          columns={columns}
          defaultSort={{ key: "cost", desc: true }}
          onOpen={(g) => navigate(planFor(g))}
          rowNoun={["vendor", "vendors"]}
          exportName="Suggested POs"
          viewsSlot={slot}
          pageFilters={false}
          empty={groups.length ? "Every vendor with something to order now is hidden or snoozed." : "Nothing to order today."}
        />
      </div>
    </section>
  );
}

/** "Shenzhen Speed Sources" → "SS". */
const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * Hide or snooze a vendor's card (Mitch, 2026-11-03: "a way to snooze or hide cards (vendors we no longer utilize etc),
 * and a proper way to view hidden and unhide those"): snoozed, it's back on its day; hidden, until shown again from
 * "N hidden". The person's own (suggested_po_hidden); the Purchasing plan still shows the vendor.
 */
function HideCard({ vendor, row, today }: { vendor: PlanVendor; row: SuggestedPoHidden | undefined; today: string }) {
  const [date, setDate] = useState("");
  const name = vendor.short_name || vendor.name;
  const save = async (until: string | null, close: () => void) => {
    const ok = row
      ? await mutate("suggested_po_hidden.update", { id: row.id, patch: { until } })
      : await mutate("suggested_po_hidden.create", { id: crypto.randomUUID(), vendor_id: vendor.id, until });
    if (ok) close();
  };
  return (
    <Popover
      width={260}
      button={(toggle, open) => (
        <button
          type="button"
          title={`Hide or snooze ${name}'s card`}
          onClick={toggle}
          className={cx("rounded p-1 hover:bg-hover hover:text-text", open ? "text-text" : "text-faint")}
        >
          <EyeOff className="size-3.5" />
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-1.5">
          <div className="font-medium">Hide {name}'s card</div>
          <div className="text-[12px] text-muted">Only on your Today. The Purchasing plan still shows {name}.</div>
          <div className="flex flex-wrap gap-1">
            {(
              [
                ["1 week", 7],
                ["2 weeks", 14],
                ["1 month", 30],
              ] as const
            ).map(([label, days]) => (
              <Button key={label} size="sm" variant="secondary" onClick={() => void save(addDays(today, days), close)}>
                Snooze {label}
              </Button>
            ))}
          </div>
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (date > today) void save(date, close);
            }}
          >
            <span className="text-[12px] text-muted">Until</span>
            <Input type="date" value={date} min={addDays(today, 1)} onChange={(e) => setDate(e.target.value)} className="h-7 flex-1 text-[12px]" />
            <Button size="sm" variant="secondary" type="submit" disabled={!(date > today)}>
              Snooze
            </Button>
          </form>
          <Button size="sm" variant="primary" icon={<EyeOff className="size-3.5" />} onClick={() => void save(null, close)}>
            Hide until I show it again
          </Button>
        </div>
      )}
    </Popover>
  );
}

/** "N hidden": the person's hidden and snoozed cards, what each would suggest now, and Show again. */
function HiddenCards({
  hidden,
  groups,
  today,
}: {
  hidden: SuggestedPoHidden[];
  groups: { vendor: PlanVendor; rows: ItemPlanResult[]; cost: Map<string, number> }[];
  today: string;
}) {
  const vendors = useRows("vendor");
  const byVendor = new Map(groups.map((g) => [g.vendor.id, g]));
  const named = hidden
    .map((h) => {
      const v = vendors.find((x) => x.id === h.vendor_id);
      return { h, name: v ? v.short_name || v.name : "A vendor", g: byVendor.get(h.vendor_id) };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Popover
      width={340}
      button={(toggle) => (
        <button type="button" onClick={toggle} className="flex items-center gap-1 text-[12px] text-muted hover:text-text hover:underline">
          <EyeOff className="size-3.5" /> {hidden.length} hidden
        </button>
      )}
    >
      {() => (
        <div className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto">
          <div className="pb-1 font-medium">Hidden from your Suggested POs</div>
          {named.map(({ h, name, g }) => (
            <div key={h.id} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-hover">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{name}</span>
                <span className="block truncate text-[11px] text-muted">
                  {h.until ? `Snoozed until ${shortDate(h.until.slice(0, 10), today)}` : "Hidden"}
                  {" · "}
                  {g
                    ? `${plural(g.rows.length, "SKU")} to order now${g.cost.size ? `, ${[...g.cost].map(([cur, n]) => money(Math.round(n * 100) / 100, cur)).join(" + ")}` : ""}`
                    : "nothing to order now"}
                </span>
              </span>
              <Button size="sm" variant="ghost" icon={<Eye className="size-3.5" />} onClick={() => void mutate("suggested_po_hidden.remove", { id: h.id })}>
                Show again
              </Button>
            </div>
          ))}
        </div>
      )}
    </Popover>
  );
}
